import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ChartDrawingController } from '../chart-drawing-controller';
import { DRAWING_TOOL_GROUPS, ToolGroup, ToolUiMeta } from '../drawing-tools.catalog';
import { TemaService } from 'src/app/services/tema.services';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-drawing-toolbar',
  templateUrl: './drawing-toolbar.component.html',
  styleUrls: ['./drawing-toolbar.component.scss'],
})
export class DrawingToolbarComponent implements OnInit, OnDestroy {
  @Input() controller!: ChartDrawingController;

  public groups: ToolGroup[] = DRAWING_TOOL_GROUPS
    .map(g => ({ ...g, tools: g.tools.filter(t => !t.hidden) }))
    .filter(g => g.tools.length > 0);

  public isDark = false;
  public flyout: string | null = null;
  public activeTool: string | null = null;
  public canUndo = false;
  public canRedo = false;
  public showMorePanel = false;
  public importJsonText = '';
  public showImportBox = false;

  /** último tool usado por grupo (el botón del grupo lo repite, como TradingView) */
  private groupPrimary: Record<string, string> = {};
  private subs: Subscription[] = [];
  private flyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private temaService: TemaService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.subs.push(
      this.temaService.valDarkTheme.subscribe(v => (this.isDark = v)),
      this.controller.activeTool$.subscribe(t => (this.activeTool = t)),
      this.controller.canUndo$.subscribe(v => (this.canUndo = v)),
      this.controller.canRedo$.subscribe(v => (this.canRedo = v)),
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    if (this.flyoutCloseTimer) clearTimeout(this.flyoutCloseTimer);
  }

  // ── flyouts ──────────────────────────────────────────────────────────

  public openFlyout(groupId: string): void {
    if (this.flyoutCloseTimer) { clearTimeout(this.flyoutCloseTimer); this.flyoutCloseTimer = null; }
    this.flyout = groupId;
  }

  public scheduleCloseFlyout(): void {
    if (this.flyoutCloseTimer) clearTimeout(this.flyoutCloseTimer);
    this.flyoutCloseTimer = setTimeout(() => { this.flyout = null; this.flyoutCloseTimer = null; }, 150);
  }

  // ── selección de herramienta ─────────────────────────────────────────

  public selectCursor(): void {
    this.controller.setActiveTool(null);
  }

  public selectTool(tool: ToolUiMeta): void {
    this.groupPrimary[this.groupOf(tool.type)] = tool.type;
    this.controller.setActiveTool(tool.type);
    this.flyout = null;
  }

  public selectGroup(group: ToolGroup): void {
    const primary = this.groupPrimary[group.id] ?? group.tools[0]?.type;
    const tool = group.tools.find(t => t.type === primary) ?? group.tools[0];
    if (tool) this.selectTool(tool);
  }

  public groupIcon(group: ToolGroup): string {
    const primary = this.groupPrimary[group.id];
    return group.tools.find(t => t.type === primary)?.icon ?? group.tools[0]?.icon ?? group.icon;
  }

  public isGroupActive(group: ToolGroup): boolean {
    return !!this.activeTool && group.tools.some(t => t.type === this.activeTool);
  }

  private groupOf(type: string): string {
    return this.groups.find(g => g.tools.some(t => t.type === type))?.id ?? '';
  }

  // ── acciones globales ────────────────────────────────────────────────

  public confirmClearAll(): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: { msj: '¿Eliminar todos los dibujos del gráfico?', icon: 'delete' },
    });
    ref.afterClosed().subscribe(ok => { if (ok) this.controller.clearAll(); });
  }

  public toggleObjectTree(): void {
    this.controller.objectTreeOpen$.next(!this.controller.objectTreeOpen$.value);
  }

  public exportToClipboard(): void {
    const json = this.controller.exportJson();
    navigator.clipboard?.writeText(json).catch(() => {});
    this.showMorePanel = false;
  }

  public importFromText(): void {
    if (this.importJsonText.trim()) {
      this.controller.importJson(this.importJsonText.trim());
      this.importJsonText = '';
    }
    this.showImportBox = false;
    this.showMorePanel = false;
  }
}
